import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiRefreshCw, FiSearch, FiX } from 'react-icons/fi';
import { usePoints } from '../hooks/usePoints';
import '../styles/points.scss';

const TABS = [
    { key: 'settings', label: 'الإعدادات' },
    { key: 'tasks', label: 'المهام' },
    { key: 'review', label: 'المراجعة' },
    { key: 'users', label: 'الأرصدة' },
    { key: 'stats', label: 'الإحصائيات' },
];

const SUBMISSION_TYPES = { feedback: 'ملاحظات واقتراحات', bug_report: 'بلاغ عن مشكلة', experience: 'مشاركة تجربة', recommend: 'نصيحة للعملاء الجدد' };
const formatDateTime = (value) => (value ? new Date(value).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const pointValue = (perDinar) => (perDinar > 0 ? Math.round((1 / perDinar) * 1000) / 1000 : 0);

// ---------- program settings ----------
const SettingsTab = ({ config, defaults, onSave, program }) => {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (config) setForm({
            enabled: config.enabled,
            pointsPerDinar: config.pointsPerDinar,
            driverCreditMultiplier: config.driverCreditMultiplier,
            maxRedeemDinarsPerTrip: config.maxRedeemDinarsPerTrip,
            minReviewLength: config.minReviewLength,
            maxRedeemDinarsPerSubscription: config.maxRedeemDinarsPerSubscription,
            ratingBadgeThreshold: config.ratingBadgeThreshold,
            ratingBadgeMinRatings: config.ratingBadgeMinRatings,
            competitiveTripsPerReward: config.competitiveTripsPerReward,
            termsText: config.termsText,
        });
    }, [config]);
    if (!form) return null;
    const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const save = async () => {
        setSaving(true);
        await onSave(program === 'driver' ? {
            enabled: form.enabled,
            pointsPerDinar: Number(form.pointsPerDinar),
            maxRedeemDinarsPerSubscription: Number(form.maxRedeemDinarsPerSubscription),
            ratingBadgeThreshold: Number(form.ratingBadgeThreshold),
            ratingBadgeMinRatings: Number(form.ratingBadgeMinRatings),
            competitiveTripsPerReward: Number(form.competitiveTripsPerReward),
            termsText: form.termsText,
        } : {
            enabled: form.enabled,
            pointsPerDinar: Number(form.pointsPerDinar),
            driverCreditMultiplier: Number(form.driverCreditMultiplier),
            maxRedeemDinarsPerTrip: Number(form.maxRedeemDinarsPerTrip),
            minReviewLength: Number(form.minReviewLength),
            termsText: form.termsText,
        });
        setSaving(false);
    };
    const per = Number(form.pointsPerDinar) || 0;
    return (
        <div className="pts-card form">
            <label className="switch-row">
                <input type="checkbox" checked={Boolean(form.enabled)} onChange={set('enabled')} />
                <span>تفعيل برنامج النقاط {program === 'driver' ? 'للسائقين' : 'للمستخدمين'} (عند الإيقاف لا تُكتسب نقاط ولا يمكن استبدالها)</span>
            </label>
            <div className="grid">
                <label>
                    عدد النقاط لكل 1 د.ل
                    <input type="number" min="1" value={form.pointsPerDinar} onChange={set('pointsPerDinar')} />
                    <small>قيمة النقطة الواحدة: <b className="num">{pointValue(per)}</b> د.ل</small>
                </label>
                {program === 'driver' ? (
                    <>
                        <label>
                            أقصى خصم بالنقاط للاشتراك (د.ل)
                            <input type="number" min="0" value={form.maxRedeemDinarsPerSubscription} onChange={set('maxRedeemDinarsPerSubscription')} />
                            <small>0 = بدون حد · يُطبّق فقط على الباقة التي تبدأ الآن</small>
                        </label>
                        <label>
                            حد التقييم لشارة التحقق
                            <input type="number" min="1" max="5" step="0.1" value={form.ratingBadgeThreshold} onChange={set('ratingBadgeThreshold')} />
                            <small>يحصل السائق على الشارة ونقاطها عندما يصل متوسط تقييمه لهذا الحد، ويفقدها (وتُخصم نقاطها) إذا انخفض عنه</small>
                        </label>
                        <label>
                            أقل عدد تقييمات للشارة
                            <input type="number" min="1" value={form.ratingBadgeMinRatings} onChange={set('ratingBadgeMinRatings')} />
                            <small>لا تُحتسب الشارة قبل هذا العدد من التقييمات</small>
                        </label>
                        <label>
                            عدد الرحلات التنافسية لكل مكافأة
                            <input type="number" min="1" value={form.competitiveTripsPerReward} onChange={set('competitiveTripsPerReward')} />
                            <small>رحلة بسعر لا يزيد عن سعر العميل المقترح وتقييم 4 نجوم فأكثر</small>
                        </label>
                    </>
                ) : (
                    <>
                        <label>
                            مضاعف رصيد السائق
                            <input type="number" min="0" max="10" step="0.5" value={form.driverCreditMultiplier} onChange={set('driverCreditMultiplier')} />
                            <small>يُضاف لرصيد السائق هذا المضاعف من قيمة الخصم (مثل الكوبونات)</small>
                        </label>
                        <label>
                            أقصى خصم بالنقاط للرحلة (د.ل)
                            <input type="number" min="0" value={form.maxRedeemDinarsPerTrip} onChange={set('maxRedeemDinarsPerTrip')} />
                            <small>0 = بدون حد</small>
                        </label>
                        <label>
                            أقل عدد أحرف لتقييم السائق
                            <input type="number" min="0" value={form.minReviewLength} onChange={set('minReviewLength')} />
                            <small>حتى يحصل المستخدم على نقاط مهمة «تقييم السائق»</small>
                        </label>
                    </>
                )}
            </div>
            <label>
                شروط استخدام النقاط (تظهر للمستخدم)
                <textarea rows={6} value={form.termsText} onChange={set('termsText')} />
            </label>
            <div className="actions">
                <button type="button" className="btn ghost" onClick={() => setForm((p) => ({ ...p, termsText: defaults?.termsText || '' }))}>استعادة الشروط الافتراضية</button>
                <button type="button" className="btn primary" disabled={saving} onClick={save}>{saving ? 'جارٍ الحفظ...' : 'حفظ'}</button>
            </div>
        </div>
    );
};

// ---------- tasks ----------
const TasksTab = ({ config, defaults, onSave }) => {
    const [tasks, setTasks] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (config) setTasks(Object.fromEntries(Object.entries(config.tasks).map(([k, t]) => [k, { enabled: t.enabled, points: t.points, title: t.title, description: t.description }])));
    }, [config]);
    if (!tasks || !defaults) return null;
    const update = (key, field, value) => setTasks((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    const save = async () => {
        setSaving(true);
        await onSave({ tasks: Object.fromEntries(Object.entries(tasks).map(([k, t]) => [k, { ...t, points: Number(t.points) }])) });
        setSaving(false);
    };
    return (
        <div className="pts-card">
            <p className="muted">النقاط التي يحصل عليها المستخدم لكل مهمة. يمكنك تعديل الاسم والوصف كما يظهران في التطبيق، أو إيقاف أي مهمة.</p>
            <div className="task-table">
                {defaults.tasks.map((def, i) => {
                    const t = tasks[def.key];
                    return (
                        <div key={def.key} className={`task-row ${t.enabled ? '' : 'off'}`}>
                            <span className="idx num">{i + 1}</span>
                            <label className="toggle" title={t.enabled ? 'مفعّلة' : 'متوقفة'}>
                                <input type="checkbox" checked={t.enabled} onChange={(e) => update(def.key, 'enabled', e.target.checked)} />
                            </label>
                            <div className="task-text">
                                <input value={t.title} placeholder={def.title} onChange={(e) => update(def.key, 'title', e.target.value)} />
                                <textarea rows={2} value={t.description} placeholder={def.description} onChange={(e) => update(def.key, 'description', e.target.value)} />
                                {def.review ? <small>تُضاف النقاط بعد موافقة الإدارة من تبويب «المراجعة»</small> : null}
                                {def.kind === 'per_platform' ? <small>لكل منصة لها رابط في الإعدادات ← روابط التواصل الاجتماعي</small> : null}
                            </div>
                            <label className="pts-input">
                                <input type="number" min="0" value={t.points} onChange={(e) => update(def.key, 'points', e.target.value)} />
                                <span>نقطة</span>
                            </label>
                        </div>
                    );
                })}
            </div>
            <div className="actions">
                <button type="button" className="btn primary" disabled={saving} onClick={save}>{saving ? 'جارٍ الحفظ...' : 'حفظ المهام'}</button>
            </div>
        </div>
    );
};

// ---------- submissions review ----------
const ReviewTab = ({ api, config }) => {
    const [status, setStatus] = useState('pending');
    const [type, setType] = useState('');
    const [data, setData] = useState(null);
    const [pointsDraft, setPointsDraft] = useState({});
    const [busy, setBusy] = useState(null);

    const load = useCallback(async (before = '') => {
        const json = await api.listSubmissions({ status, type, before });
        if (json) setData((prev) => (before && prev ? { ...json, items: [...prev.items, ...json.items] } : json));
    }, [api, status, type]);
    useEffect(() => { load(); }, [status, type]); // eslint-disable-line react-hooks/exhaustive-deps

    const decide = async (item, approve) => {
        setBusy(item._id);
        const ok = approve
            ? await api.approveSubmission(item._id, pointsDraft[item._id] !== undefined && pointsDraft[item._id] !== '' ? Number(pointsDraft[item._id]) : undefined)
            : await api.rejectSubmission(item._id, window.prompt('سبب الرفض (اختياري)') || '');
        setBusy(null);
        if (ok) load();
    };

    const recommend = data?.recommend || {};
    const yes = recommend.yes || 0;
    const no = recommend.no || 0;
    return (
        <div className="pts-card">
            <div className="toolbar">
                <div className="seg">
                    {[['pending', 'بانتظار المراجعة'], ['approved', 'مقبولة'], ['rejected', 'مرفوضة']].map(([v, l]) => (
                        <button key={v} type="button" className={status === v ? 'active' : ''} onClick={() => setStatus(v)}>
                            {l}{v === 'pending' && data ? <span className="count num">{Object.values(data.pending || {}).reduce((a, b) => a + b, 0)}</span> : null}
                        </button>
                    ))}
                </div>
                <select value={type} onChange={(e) => setType(e.target.value)}>
                    <option value="">كل الأنواع</option>
                    {Object.entries(SUBMISSION_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button type="button" className="btn ghost" onClick={() => load()}><FiRefreshCw /></button>
            </div>
            {yes + no > 0 ? (
                <p className="muted">«هل تنصح بجاهز؟»: <b className="num">{yes}</b> نعم · <b className="num">{no}</b> لا · <b className="num">{Math.round((yes / (yes + no)) * 100)}%</b> ينصحون به</p>
            ) : null}
            {!data ? <div className="placeholder">جارٍ التحميل...</div> : data.items.length === 0 ? <div className="placeholder">لا توجد طلبات</div> : data.items.map((item) => (
                <div key={item._id} className="sub-row">
                    <div className="sub-head">
                        <span className={`type-chip ${item.type}`}>{SUBMISSION_TYPES[item.type]}</span>
                        <b>{item.userName}</b>
                        <span className="muted num">{item.userPhoneNumber}</span>
                        {item.appVersion ? <span className="muted">إصدار <span className="num">{item.appVersion}</span></span> : null}
                        <span className="muted num">{formatDateTime(item.createdAt)}</span>
                    </div>
                    {item.answer ? <p className="answer">{item.answer === 'yes' ? 'نعم، أنصح به' : 'لا'}</p> : null}
                    {item.message ? <p className="message">{item.message}</p> : null}
                    {item.status === 'pending' ? (
                        <div className="sub-actions">
                            <label className="pts-input small">
                                <input
                                    type="number"
                                    min="1"
                                    placeholder={String(config?.tasks?.[item.type]?.points ?? '')}
                                    value={pointsDraft[item._id] ?? ''}
                                    onChange={(e) => setPointsDraft((p) => ({ ...p, [item._id]: e.target.value }))}
                                />
                                <span>نقطة</span>
                            </label>
                            <button type="button" className="btn success small" disabled={busy === item._id} onClick={() => decide(item, true)}><FiCheck /> قبول وإضافة النقاط</button>
                            <button type="button" className="btn ghost small danger-text" disabled={busy === item._id} onClick={() => decide(item, false)}><FiX /> رفض</button>
                        </div>
                    ) : (
                        <p className="muted">
                            {item.status === 'approved' ? <>تمت الموافقة · <b className="num">+{item.pointsAwarded}</b> نقطة</> : `مرفوض${item.reviewNote ? ` · ${item.reviewNote}` : ''}`}
                            {item.reviewedBy ? ` · ${item.reviewedBy}` : ''}
                        </p>
                    )}
                </div>
            ))}
            {data?.nextBefore ? <button type="button" className="btn ghost more" onClick={() => load(data.nextBefore)}>عرض المزيد</button> : null}
        </div>
    );
};

// ---------- users ----------
const UsersTab = ({ api, program }) => {
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);
    const [adjust, setAdjust] = useState({ points: '', reason: '' });

    const search = async (e) => {
        e?.preventDefault();
        if (!q.trim()) return;
        const json = await api.searchUsers(q.trim());
        setResults(json?.users || []);
    };
    const open = async (id, before = '') => {
        const json = await api.getUser(id, before);
        if (json) setSelected((prev) => (before && prev ? { ...json, items: [...prev.items, ...json.items] } : json));
    };
    const doAdjust = async () => {
        const json = await api.adjustUser(selected.user._id, Number(adjust.points), adjust.reason);
        if (json) {
            setAdjust({ points: '', reason: '' });
            open(selected.user._id);
        }
    };

    return (
        <div className="users-layout">
            <div className="pts-card">
                <form className="search" onSubmit={search}>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={program === 'driver' ? 'ابحث برقم الهاتف أو الاسم أو رقم السائق' : 'ابحث برقم الهاتف أو الاسم'} />
                    <button type="submit" className="btn primary"><FiSearch /> بحث</button>
                </form>
                {results.map((u) => (
                    <button key={u._id} type="button" className={`user-row ${selected?.user?._id === u._id ? 'active' : ''}`} onClick={() => open(u._id)}>
                        <span><b>{u.name}</b> {u.driverNumber ? <span className="muted num">#{u.driverNumber}</span> : null} <span className="muted num">{u.phoneNumber}</span></span>
                        <span className="num pts">{u.points || 0}</span>
                    </button>
                ))}
            </div>
            {selected ? (
                <div className="pts-card">
                    <h3>{selected.user.name} <span className="muted num">{selected.user.phoneNumber}</span>{selected.user.ratingBadge ? <span className="badge-chip">سائق موثّق</span> : null}</h3>
                    <div className="kpis">
                        <div><span className="label">الرصيد</span><b className="num">{selected.user.points || 0}</b></div>
                        <div><span className="label">الرحلات</span><b className="num">{selected.user.numberOfTrips || 0}</b></div>
                        <div><span className="label">{program === 'driver' ? 'سائقون مدعوون' : 'أصدقاء مُحالون'}</span><b className="num">{selected.user.referrals || 0}</b></div>
                        <div><span className="label">{program === 'driver' ? 'كود الدعوة' : 'كود الإحالة'}</span><b className="num">{selected.user.referralCode || '—'}</b></div>
                    </div>
                    <div className="adjust">
                        <input type="number" placeholder="+50 أو -20" value={adjust.points} onChange={(e) => setAdjust((p) => ({ ...p, points: e.target.value }))} />
                        <input placeholder="السبب (يظهر في سجل المستخدم)" value={adjust.reason} onChange={(e) => setAdjust((p) => ({ ...p, reason: e.target.value }))} />
                        <button type="button" className="btn primary" disabled={!Number(adjust.points) || !adjust.reason.trim()} onClick={doAdjust}>تعديل الرصيد</button>
                    </div>
                    <div className="ledger">
                        {selected.items.map((h) => (
                            <div key={h._id} className="ledger-row">
                                <span>{h.title}{h.note ? <span className="muted"> · {h.note}</span> : null}</span>
                                <span className="muted num">{formatDateTime(h.createdAt)}</span>
                                <b className={`num ${h.points >= 0 ? 'plus' : 'minus'}`}>{h.points > 0 ? `+${h.points}` : h.points}</b>
                            </div>
                        ))}
                        {selected.nextBefore ? <button type="button" className="btn ghost more" onClick={() => open(selected.user._id, selected.nextBefore)}>عرض المزيد</button> : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

// ---------- stats ----------
const StatsTab = ({ api, program }) => {
    const today = new Date().toISOString().slice(0, 10);
    const monthStart = `${today.slice(0, 8)}01`;
    const [from, setFrom] = useState(monthStart);
    const [to, setTo] = useState(today);
    const [stats, setStats] = useState(null);
    const load = useCallback(async () => {
        setStats(await api.getStats(new Date(`${from}T00:00:00`).toISOString(), new Date(`${to}T23:59:59`).toISOString()));
    }, [api, from, to]);
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <div className="pts-card">
            <div className="toolbar">
                <label>من <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                <label>إلى <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
                <button type="button" className="btn primary" onClick={load}>عرض</button>
            </div>
            {stats ? (
                <>
                    <div className="kpis big">
                        <div><span className="label">نقاط مكتسبة</span><b className="num">{stats.issued}</b></div>
                        <div><span className="label">نقاط مستبدلة</span><b className="num">{stats.redeemed}</b></div>
                        <div>
                            <span className="label">خصومات بالنقاط</span>
                            <b><span className="num">{stats.discountDinars}</span> د.ل</b>
                            <small><span className="num">{program === 'driver' ? stats.redeemedSubscriptions : stats.redeemedTrips}</span> {program === 'driver' ? 'اشتراك' : 'رحلة'}</small>
                        </div>
                        {program === 'driver' ? (
                            <div><span className="label">خُصمت (فقدان شارة التحقق)</span><b className="num">{stats.deducted || 0}</b></div>
                        ) : (
                            <div><span className="label">أضيف لأرصدة السائقين</span><b><span className="num">{stats.driverCredit}</span> د.ل</b></div>
                        )}
                        <div><span className="label">{program === 'driver' ? 'رصيد قائم لدى السائقين' : 'رصيد قائم لدى المستخدمين'}</span><b className="num">{stats.outstandingPoints}</b><small>≈ <span className="num">{Math.floor(stats.outstandingPoints / (stats.pointsPerDinar || 10))}</span> د.ل · <span className="num">{stats.usersWithPoints}</span> {program === 'driver' ? 'سائق' : 'مستخدم'}</small></div>
                    </div>
                    <h4>المهام الأكثر منحاً للنقاط</h4>
                    {stats.tasks.length === 0 ? <p className="muted">لا توجد نقاط في هذه الفترة</p> : stats.tasks.map((t) => (
                        <div key={t.key} className="bar-row">
                            <span>{t.title}</span>
                            <div className="bar"><div style={{ width: `${Math.max(4, Math.round((t.points / stats.tasks[0].points) * 100))}%` }} /></div>
                            <b className="num">{t.points}</b>
                            <span className="muted num">{t.count}×</span>
                        </div>
                    ))}
                </>
            ) : <div className="placeholder">جارٍ التحميل...</div>}
        </div>
    );
};

export const Points = () => {
    const user = useSelector((state) => state.userController.user);
    const navigate = useNavigate();
    const [program, setProgram] = useState('user');
    const api = usePoints(program);
    const [tab, setTab] = useState('settings');

    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);
    if (!user) return null;

    return (
        <section className="container points-page">
            <header className="pts-header">
                <div className="program-switch">
                    {[['user', 'المستخدمين'], ['driver', 'السائقين']].map(([v, l]) => (
                        <button key={v} type="button" className={program === v ? 'active' : ''} onClick={() => setProgram(v)}>{l}</button>
                    ))}
                </div>
                <div>
                    <h2>النقاط</h2>
                    <p className="muted">
                        {program === 'driver' ? 'برنامج «نقاطي» للسائقين (خصم على الاشتراكات، منفصل عن نقاط النشاط)' : 'برنامج «نقاطي» للمستخدمين (خصم على الرحلات)'}
                        {api.config ? <> · النقطة = <b className="num">{pointValue(api.config.pointsPerDinar)}</b> د.ل · {api.config.enabled ? 'مفعّل' : 'متوقف'}</> : null}
                    </p>
                </div>
            </header>
            <div className="pts-tabs">
                {TABS.map((t) => (
                    <button key={t.key} type="button" className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>{t.label}</button>
                ))}
            </div>
            {!api.config ? <div className="pts-card placeholder">جارٍ التحميل...</div> : (
                <>
                    {tab === 'settings' ? <SettingsTab key={program} program={program} config={api.config} defaults={api.defaults} onSave={api.saveConfig} /> : null}
                    {tab === 'tasks' ? <TasksTab key={program} config={api.config} defaults={api.defaults} onSave={api.saveConfig} /> : null}
                    {tab === 'review' ? <ReviewTab key={program} api={api} config={api.config} /> : null}
                    {tab === 'users' ? <UsersTab key={program} api={api} program={program} /> : null}
                    {tab === 'stats' ? <StatsTab key={program} api={api} program={program} /> : null}
                </>
            )}
        </section>
    );
};
